import { CloudAttachment } from '@/services';

export interface AttachmentFolderNode {
    attachment?: CloudAttachment;
    files: CloudAttachment[];
    folders: Map<string, AttachmentFolderNode>;
    key: string;
    name: string;
}

const createFolderNode = (key: string, name: string): AttachmentFolderNode => ({
    files: [],
    folders: new Map(),
    key,
    name,
});

export const buildAttachmentTree = (attachments: CloudAttachment[]) => {
    const root = createFolderNode('', 'Attachments');

    attachments.forEach((attachment) => {
        const pathSegments = (attachment.relativePath || attachment.name).split('/').filter(Boolean);
        const folderSegments = attachment.kind === 'folder' ? pathSegments : pathSegments.slice(0, -1);
        let current = root;

        folderSegments.forEach((segment, index) => {
            const key = folderSegments.slice(0, index + 1).join('/');
            const existing = current.folders.get(segment);

            if (existing) {
                current = existing;
                return;
            }

            const folder = createFolderNode(key, segment);

            current.folders.set(segment, folder);
            current = folder;
        });

        if (attachment.kind === 'folder') {
            current.attachment = attachment;
        } else {
            current.files.push(attachment);
        }
    });

    return root;
};

export const getFolderItemCount = (folder: AttachmentFolderNode): number =>
    folder.files.length +
    Array.from(folder.folders.values()).reduce((total, child) => total + getFolderItemCount(child), 0);

export const getFolderFileIds = (folder: AttachmentFolderNode): string[] => [
    ...folder.files.map((file) => file.id),
    ...Array.from(folder.folders.values()).flatMap(getFolderFileIds),
];

/**
 * Resolves the concrete source and destination folders whose contents are
 * changing during an optimistic move. Root is intentionally omitted because
 * it has no folder row to annotate.
 */
export const getAffectedAttachmentFolderIds = (
    attachments: CloudAttachment[],
    sourceAttachmentIds: string[],
    destinationFolderId?: string,
): Set<string> => {
    const folderIdByPath = new Map(
        attachments
            .filter((attachment) => attachment.kind === 'folder')
            .map((folder) => [folder.relativePath ?? folder.name, folder.id] as const),
    );
    const affectedFolderIds = new Set<string>();

    sourceAttachmentIds.forEach((sourceAttachmentId) => {
        const source = attachments.find((attachment) => attachment.id === sourceAttachmentId);
        const sourcePath = source?.relativePath ?? source?.name;

        if (!sourcePath) {
            return;
        }

        const sourceParentPath = sourcePath.split('/').filter(Boolean).slice(0, -1).join('/');
        const sourceParentFolderId = folderIdByPath.get(sourceParentPath);

        if (sourceParentFolderId) {
            affectedFolderIds.add(sourceParentFolderId);
        }
    });

    if (destinationFolderId) {
        affectedFolderIds.add(destinationFolderId);
    }

    return affectedFolderIds;
};
